import { FC, useMemo, useState } from 'react';
import { ButtonProps } from '@tuic/web-compositions/core/Button';
import { AlertProps } from '@tuic/web-compositions/core/Alert';
import { GeneralView } from '@modules/get-slash-internet-card/components/dialog-views/general-view';
import { ConnectionStatusSymbol } from '@modules/get-slash-internet-card/components/connection-status-symbol';
import { ConsumptionProgress } from '@modules/get-slash-internet-card/components/dialog-views/consumption-view/consumption-progress';
import {
  GetSlashInternetSession,
  GetSlashStartSessionStatus,
  StartSessionResult,
} from '@modules/get-slash-internet-card/lib/use-get-slash-internet';
import { NetworkAccessType } from '@modules/get-slash-internet-card/lib/use-network-access-type';
import {
  InternetCardViewData,
  GetInternetDataCta,
} from '@modules/get-slash-internet-card/lib/internet-card-view-data';
import {
  DocumentRenderer,
  getConnectionStatus,
  TextRenderer,
} from '@modules/get-slash-internet-card/lib/utils';
import { SmartLink } from '../../../../app/[locale]/[bookingCode]/_components/smart-link';
import { useClientTranslation } from '@lib/i18n/client';
import { TFunction } from '@tuic/i18n';

type ConsumptionViewProps = {
  data: InternetCardViewData['consumptionView'];
  session: GetSlashInternetSession;
  networkAccessType: NetworkAccessType | undefined;
  handleRefresh: () => Promise<void> | void;
  handleTariffToggle: () => Promise<StartSessionResult | undefined>;
  pendingRefresh: boolean;
  pendingToggle: boolean;
  textRenderer: TextRenderer;
  documentRenderer: DocumentRenderer;
  isMobile?: boolean;
};

const transformCtasToButtonProps = (
  ctas: GetInternetDataCta[] | undefined,
): ButtonProps[] => {
  return (ctas ?? []).map(cta => ({
    variant: cta.variant,
    asChild: true,
    children: <SmartLink href={cta.link}>{cta.label}</SmartLink>,
  }));
};

export const ConsumptionView: FC<ConsumptionViewProps> = ({
  data,
  session,
  networkAccessType,
  handleRefresh,
  handleTariffToggle: handleTariffToggleArg,
  pendingRefresh,
  pendingToggle,
  documentRenderer,
  textRenderer,
  isMobile,
}) => {
  const { t } = useClientTranslation();
  const [tariffToggleResult, setTariffToggleResult] = useState<
    StartSessionResult | undefined
  >();

  const { active } = session;
  const { textActive, textPaused } = data ?? {};

  // time-based tariffs are also considered as flat rate (time based tariffs have maxVolume = 0)
  // to ensure there is no consumption progress bar shown
  // in the future this maybe could be extended to show time-based progress bars
  const isFlatRate =
    typeof session.maxVolume === 'undefined' || session.maxVolume === 0;

  const progressBar = useMemo(() => {
    if (isFlatRate) {
      return null;
    }

    return <ConsumptionProgress session={session} />;
  }, [session, isFlatRate]);

  const connectionTag = useMemo(() => {
    return (
      <ConnectionStatusSymbol
        size="md"
        status={getConnectionStatus(networkAccessType, session)}
      />
    );
  }, [networkAccessType, session]);

  const text = useMemo(() => {
    return active ? textActive : textPaused;
  }, [active, textActive, textPaused]);

  const pauseButtonLabel = data?.buttonLabels?.pause ?? t('Tarif pausieren');
  const resumeButtonLabel = data?.buttonLabels?.resume ?? t('Tarif fortsetzen');

  const handleTariffToggle = async () => {
    setTariffToggleResult(undefined);
    setTariffToggleResult(await handleTariffToggleArg());
  };

  const buttons: ButtonProps[] = [];

  // Always add the pause/continue button first to maintain consistent position
  buttons.push({
    children: session.active ? pauseButtonLabel : resumeButtonLabel,
    variant: session.active ? 'secondary' : 'primary',
    iconLeft: session.active ? 'pause' : 'play',
    onClick: handleTariffToggle,
    indicating: pendingToggle,
  });

  // Add refresh button second when session is active (only for non-flat rates)
  if (!isFlatRate && session.active) {
    buttons.push({
      children: data?.buttonLabels?.refresh ?? t('Verbrauch aktualisieren'),
      variant: 'primary',
      onClick: handleRefresh,
      indicating: pendingRefresh,
    });
  }

  // Add CTAs when session is not active (only for non-flat rates)
  if (!isFlatRate && !session.active) {
    transformCtasToButtonProps(data?.ctas).forEach(buttonProps =>
      buttons.push(buttonProps),
    );
  }

  const MobileView = () => (
    <GeneralView
      centered
      text={documentRenderer(text)}
      connectionTag={connectionTag}
      progressBar={progressBar}
      alert={getAlertProps(t, tariffToggleResult)}
      buttons={
        session.active
          ? [...buttons].reverse() // tarif pausieren
          : buttons // tarif fortsetzen
      }
    />
  );

  const DesktopView = () => (
    <GeneralView
      centered
      text={documentRenderer(text)}
      connectionTag={connectionTag}
      progressBar={progressBar}
      alert={getAlertProps(t, tariffToggleResult)}
      buttons={
        session.active
          ? buttons // tarif pausieren
          : [...buttons].reverse() // tarif fortsetzen
      }
    />
  );

  return isMobile ? <MobileView /> : <DesktopView />;
};

const getAlertProps = (
  t: TFunction,
  result: StartSessionResult | undefined,
): AlertProps | undefined => {
  if (!result) {
    return undefined;
  }

  const { status, maxClients } = result;

  if (status === GetSlashStartSessionStatus.DeviceLimitExceeded) {
    const deviceLimitText = maxClients
      ? t(
          'Es sind bereits {{count}} Geräte gleichzeitig mit diesem Tarif online. Bitte pausieren Sie die Internetverbindung auf einem anderen Gerät, um den Tarif auf diesem Gerät zu nutzen.',
          { count: maxClients },
        )
      : t(
          'Die maximale Anzahl gleichzeitig aktiver Geräte für diesen Tarif ist erreicht. Bitte pausieren Sie die Internetverbindung auf einem anderen Gerät, um den Tarif auf diesem Gerät zu nutzen.',
        );

    return {
      mode: 'attention',
      headline: t('Maximale Geräte-Anzahl erreicht'),
      children: deviceLimitText,
    };
  }

  if (status === GetSlashStartSessionStatus.NoTariff) {
    return {
      mode: 'attention',
      headline: t('Kein gebuchter Tarif verfügbar'),
      children: t('Bitte buchen Sie zunächst einen Tarif, um fortzufahren.'),
    };
  }

  if (status === GetSlashStartSessionStatus.GeneralError) {
    return {
      mode: 'error',
      headline: t('Ein Fehler aufgetreten.'),
      children: t(
        'Bitte versuchen Sie es später erneut. Sollte das Problem unverändert bestehen bleiben, kontaktieren Sie bitte unser Service Team.',
      ),
    };
  }

  return undefined;
};
